import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { first } from 'rxjs/operators';
import { PaymentCondition } from '../interface/paymentCondition';
import { environment } from '../../environments/environment';
import { firstValueFrom } from 'rxjs';
import { DataService } from './data.service';
import { ApiFinanceOUT, ReplaceOsPaymentConditionsRequest, ReplaceOsPaymentConditionsResponse } from '../interface/ro-finance';
import { DisableOrActive } from '../interface/disableOrActive';

@Injectable({
  providedIn: 'root'
})
export class PaymentService {
  private _isFromMatrix = true;


  readonly SPECIAL_DESCRIPTIONS = ["999", "Pagamento à vista"];

  constructor(
    private http: HttpClient,
    private _dataService: DataService
  ) { }

  async getAll(cnpj?: string): Promise<PaymentCondition[]> {
    const url = `${environment.mkgoURL}/api/v1/paymentConditions`;
    const header = await firstValueFrom(this._dataService.httpOptions(cnpj || this._isFromMatrix));
    const resp: PaymentCondition[] = await this.http.get(url, header).pipe(first()).toPromise() as any;
    resp.map(payment => payment.form = (payment.form as any).padStart(2, '0'))
    return resp.reverse();
  }

  async getById(id: string): Promise<PaymentCondition> {
    const url = `${environment.mkgoURL}/api/v1/paymentConditions/${id}`;
    const header = await firstValueFrom(this._dataService.httpOptions(this._isFromMatrix));
    const resp: any = await this.http.get(url, header).pipe(first()).toPromise();
    return resp;
  }

  async create(paymentCondition: PaymentCondition): Promise<string> {
    const url = `${environment.mkgoURL}/api/v1/paymentConditions`;
    const header = await firstValueFrom(this._dataService.httpOptions(this._isFromMatrix));
    const resp: any = await firstValueFrom(this.http.post(url, paymentCondition, header));
    return resp['id'];
  }

  async update(paymentCondition: PaymentCondition): Promise<string> {
    const url = `${environment.mkgoURL}/api/v1/paymentConditions/${paymentCondition._id}`;
    const payment = paymentCondition;
    delete payment._id;
    const header = await firstValueFrom(this._dataService.httpOptions(this._isFromMatrix));
    const resp = await firstValueFrom(this.http.put(url, JSON.stringify(payment), header));
    return resp['id'];
  }

  public async getInactives(cnpj?: string): Promise<PaymentCondition[]> {
    const url = `${environment.mkgoURL}/api/v1/paymentConditions/inactives`;
    const header = await firstValueFrom(this._dataService.httpOptions(cnpj || this._isFromMatrix));
    const resp: PaymentCondition[] = await this.http.get(url, header).pipe(first()).toPromise() as any;
    resp["paymentConditions"].map(payment => payment.form = (payment?.form as any)?.padStart(2, '0'));
    return resp["paymentConditions"].reverse();
  }

  public async inactiveOrActive(id: string, action: DisableOrActive): Promise<string> {
    const url = `${environment.mkgoURL}/api/v1/paymentConditions/${id}/${action}`;
    const header = await firstValueFrom(this._dataService.httpOptions(this._isFromMatrix));
    const resp = await firstValueFrom(this.http.put(url, {}, header));
    return resp['id'];
  }

  async addToRO(paymentConditions: ApiFinanceOUT[], roID: string) {
    const url = `${environment.mkgoURL}/api/v1/paymentConditions/os/${roID}`;
    const header = await firstValueFrom(this._dataService.httpOptions(this._isFromMatrix));
    const body = { paymentConditions };
    const resp = await this.http.post(url, body, header).pipe(first()).toPromise();
    return resp;
  }

  async removeFromOS(osID: string, paymentsId: string[]) {
    const url = `${environment.mkgoURL}/api/v1/paymentConditions/os/${osID}`;
    const options = await firstValueFrom(this._dataService.httpOptions(this._isFromMatrix));
    options["body"] = { "paymentConditions": paymentsId };
    const resp = await this.http.request("DELETE", url, options).pipe(first()).toPromise();
    return resp;
  }

  async updateIntoOS(osID: string, payment: ApiFinanceOUT) {
    const url = `${environment.mkgoURL}/api/v1/paymentConditions/os/${osID}`;
    const options = await firstValueFrom(this._dataService.httpOptions(this._isFromMatrix));
    const resp = await this.http.put(url, payment, options).pipe(first()).toPromise();
    return resp;
  }

  /**
   * Atomically replaces the complete payment-condition collection of an OS.
   * Retrying the same logical operation MUST reuse the same `operationId` so the
   * backend can recognize the replay and avoid persisting/creating anything twice.
   */
  async replaceOsPaymentConditions(osID: string, request: ReplaceOsPaymentConditionsRequest): Promise<ReplaceOsPaymentConditionsResponse> {
    const url = `${environment.mkgoURL}/api/v1/paymentConditions/os/${osID}/replace`;
    const header = await firstValueFrom(this._dataService.httpOptions(this._isFromMatrix));
    const resp = await firstValueFrom(this.http.put<ReplaceOsPaymentConditionsResponse>(url, request, header));
    return resp;
  }
}
